import { useEffect, useState, useCallback } from "react";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Mail, Send, PlayCircle, Receipt } from "lucide-react";

export default function Invoices() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [status, setStatus] = useState("vencido");
  const [page, setPage] = useState(1);
  const [running, setRunning] = useState(false);
  const perPage = 20;

  const load = useCallback(() => {
    api.get("/clients", { params: { status, page, per_page: perPage, sort: "due_date" } })
      .then((r) => setData(r.data));
  }, [status, page]);
  useEffect(() => { load(); }, [load]);

  const remind = async (id, channel) => {
    try {
      const { data } = await api.post(`/clients/${id}/remind`, { channel });
      const ok = (data.results || []).filter((x) => ["sent", "mock"].includes(x.status));
      ok.length ? toast.success(`Cobrança enviada (${ok.map((x) => x.channel).join(", ")})`) : toast.warning("Nada enviado (sem contato)");
    } catch { toast.error("Erro ao enviar"); }
  };

  const runAuto = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/reminders/run");
      toast.success(`Automação executada: ${data.sent} enviados, ${data.skipped} já enviados`);
    } catch { toast.error("Erro ao executar automação"); }
    setRunning(false);
  };

  const pages = Math.max(1, Math.ceil(data.total / perPage));
  const tabs = [["vencido", "Vencidas"], ["a_vencer", "A Vencer"], ["ativo", "Em dia"], ["", "Todas"]];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-100 flex items-center gap-2"><Receipt className="h-7 w-7 text-sky-400" /> Faturas & Cobrança</h1>
          <p className="text-sm text-slate-400 mt-1">Dispare cobranças por e-mail e SMS</p>
        </div>
        <button data-testid="run-automation-button" disabled={running} onClick={runAuto}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 disabled:opacity-50">
          <PlayCircle className="h-4 w-4" /> Rodar automação agora
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {tabs.map(([v, l]) => (
          <button key={v} data-testid={`invoice-tab-${v || "all"}`} onClick={() => { setStatus(v); setPage(1); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${status === v ? "bg-sky-500/15 text-sky-300 border-sky-500/30" : "bg-[#111827] text-slate-400 border-[#1F293D] hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-[#1F293D]">
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Plano</th>
                <th className="px-4 py-3 font-medium">Valor</th>
                <th className="px-4 py-3 font-medium">Vencimento</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Disparar</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} className="border-b border-[#1F293D] hover:bg-[#151e30]">
                  <td className="px-4 py-3">
                    <div className="text-slate-100 font-medium">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.email || c.whatsapp || "sem contato"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{c.plan || "—"}</td>
                  <td className="px-4 py-3 font-mono text-slate-200">{fmtBRL(c.value)}</td>
                  <td className="px-4 py-3 font-mono text-slate-300">{fmtDate(c.due_date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.billing_status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`invoice-email-${c.id}`} disabled={!c.email} onClick={() => remind(c.id, "email")}
                        title="Enviar e-mail" className="p-2 rounded-lg bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 disabled:opacity-30"><Mail className="h-4 w-4" /></button>
                      <button data-testid={`invoice-sms-${c.id}`} disabled={!c.whatsapp} onClick={() => remind(c.id, "sms")}
                        title="Enviar SMS" className="p-2 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 disabled:opacity-30"><Send className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Nenhuma fatura nesta categoria.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#1F293D]">
          <span className="text-xs text-slate-500">{data.total.toLocaleString("pt-BR")} faturas · página {page}/{pages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 text-xs disabled:opacity-40">Anterior</button>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 text-xs disabled:opacity-40">Próxima</button>
          </div>
        </div>
      </div>
    </div>
  );
}
